/**
 * Offline cache helpers — IndexedDB-backed.
 *  - Checklist schema cache (so the checklist renders offline).
 *  - Inspections list cache (so the dashboard works offline).
 *  - Local inspections: created while offline with a `local_` id, synced to the
 *    server (POST) by the offline queue when connectivity returns.
 */
import { kvGet, kvSet } from "@/lib/localStore";

const SCHEMA_KEY = "psc_schema_cache";
const LIST_KEY = "psc_inspections_cache";
const LOCAL_KEY = "psc_local_inspections";
const MAP_KEY = "psc_localid_map";

export const cacheSchema = (sections) => kvSet(SCHEMA_KEY, { sections, at: Date.now() });
export const getCachedSchema = async () => (await kvGet(SCHEMA_KEY))?.sections || null;

export const cacheInspectionsList = (list) => kvSet(LIST_KEY, { list, at: Date.now() });
export const getCachedInspectionsList = async () => (await kvGet(LIST_KEY)) || null;

export const isLocalId = (id) => typeof id === "string" && id.startsWith("local_");

export const createLocalInspection = async (payload) => {
  const id = `local_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const now = new Date().toISOString();
  const all = (await kvGet(LOCAL_KEY)) || {};
  all[id] = {
    ...payload, id, local: true, status: "draft",
    flagged_count: 0, score_obtained: 0, score_total: 0,
    created_at: now, updated_at: now,
  };
  await kvSet(LOCAL_KEY, all);
  return all[id];
};

export const getLocalInspections = async () => Object.values((await kvGet(LOCAL_KEY)) || {});
export const getLocalInspection = async (id) => ((await kvGet(LOCAL_KEY)) || {})[id] || null;

export const updateLocalInspection = async (id, patch) => {
  const all = (await kvGet(LOCAL_KEY)) || {};
  if (!all[id]) return null;
  all[id] = { ...all[id], ...patch, updated_at: new Date().toISOString() };
  await kvSet(LOCAL_KEY, all);
  return all[id];
};

export const removeLocalInspection = async (id) => {
  const all = (await kvGet(LOCAL_KEY)) || {};
  delete all[id];
  await kvSet(LOCAL_KEY, all);
};

export const getLocalIdMap = async () => (await kvGet(MAP_KEY)) || {};
export const mapLocalId = async (localId, realId) => {
  const m = await getLocalIdMap();
  m[localId] = realId;
  await kvSet(MAP_KEY, m);
};
